import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Progress } from '../components/ui/progress'
import { Badge } from '../components/ui/badge'
import { 
  BarChart3, 
  FileText, 
  FolderOpen, 
  TrendingUp,
  Clock,
  AlertTriangle,
  Activity,
  Calendar,
  Upload,
  Zap,
  Briefcase,
  Shield,
  Bell,
  Loader2,
  CheckCircle2
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { supabase } from '../integrations/supabase/client'
import { useToast } from '../hooks/use-toast'
import { ActionItemsCard } from '../components/ActionItemsCard'
import { RiskOverviewWidget } from '../components/dashboard/RiskOverviewWidget'
import { ImpactWidget } from '../components/dashboard/ImpactWidget'
import { useTranslation } from 'react-i18next'



interface OrganizationInfo { name?: string }
interface ProfileLike { full_name?: string; email?: string }
interface DashboardPageProps {
  organization?: OrganizationInfo | null
  profile: ProfileLike
}

// Datatyper från Supabase
interface Audit {
  id: string
  title: string
  description?: string
  status: 'pending' | 'completed' | 'initial_review' | 'detailed_analysis' | 'error'
  created_at: string
  updated_at: string
  data_input: any
  user_id: string
}

interface AuditFinding {
  id: string
  audit_id: string
  finding_type: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  description: string
  flagged_by_model: string
  created_at: string
}

interface DashboardStats {
  totalAudits: number
  activeAudits: number
  completedAudits: number
  pendingAudits: number
  totalFindings: number
  highSeverityFindings: number
  totalReports: number
}

export function DashboardPage({ organization, profile }: DashboardPageProps) {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [recentAudits, setRecentAudits] = useState<Audit[]>([])
  const [recentFindings, setRecentFindings] = useState<AuditFinding[]>([])
  const { toast } = useToast()
  const { t, i18n } = useTranslation('common')

  // Hämta dashboard-statistik
  const fetchDashboardStats = async () => {
    try {
      // Använd den funktion vi skapade i databasen
      const { data, error } = await supabase
        .rpc('get_audit_statistics')

      if (error) throw error

      if (data && data.length > 0) {
        const statsData = data[0]
        setStats({
          totalAudits: statsData.total_audits || 0,
          activeAudits: statsData.pending_audits || 0,
          completedAudits: statsData.completed_audits || 0,
          pendingAudits: statsData.pending_audits || 0,
          totalFindings: statsData.total_findings || 0,
          highSeverityFindings: statsData.high_severity_findings || 0,
          totalReports: 0 // Vi lägger till detta senare
        })
      } else {
        // Defaultvärden om inga audits finns
        setStats({
          totalAudits: 0,
          activeAudits: 0,
          completedAudits: 0,
          pendingAudits: 0,
          totalFindings: 0,
          highSeverityFindings: 0,
          totalReports: 0
        })
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
      toast({
        title: t('dashboard.toast.statsError'),
        description: t('dashboard.toast.statsErrorDescription'),
        variant: "destructive"
      })
    }
  }

  // Hämta senaste audits
  const fetchRecentAudits = async () => {
    try {
      const { data, error } = await supabase
        .from('audits')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5)

      if (error) throw error
      setRecentAudits(data || [])
    } catch (error) {
      console.error('Error fetching recent audits:', error)
    }
  }

  // Hämta senaste findings
  const fetchRecentFindings = async () => {
    try {
      const { data, error } = await supabase
        .from('audit_findings')
        .select(`
          *,
          audits!inner(title)
        `)
        .order('created_at', { ascending: false })
        .limit(5)

      if (error) throw error
      setRecentFindings(data || [])
    } catch (error) {
      console.error('Error fetching recent findings:', error)
    }
  }

  // Ladda all data när komponenten mountar
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true)
      await Promise.all([
        fetchDashboardStats(),
        fetchRecentAudits(),
        fetchRecentFindings()
      ])
      setLoading(false)
    }

    loadDashboardData()
  }, [])

  // Språklokaler för datum
  // Date locales disabled to avoid bundler resolution issues

  // Formatera datum
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return t('dashboard.time.lessThanHour')
    if (diffInHours < 24) return t('dashboard.time.hoursAgo', { hours: diffInHours })
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return t('dashboard.time.daysAgo', { days: diffInDays })
    
    return new Intl.DateTimeFormat(i18n.language || 'sv', { month: 'short', day: '2-digit' }).format(date)
  }

  // Visa laddningsskärm
  if (loading) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto" />
            <p className="text-gray-600">{t('dashboard.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-gray-200 p-8">
        <div className="space-y-6">
          {/* Welcome Text */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              {t('dashboard.welcome', { name: (profile.full_name && profile.full_name.trim().length > 0) ? profile.full_name : (profile.email || t('dashboard.defaultUser')) })}
            </h1>
            <p className="text-lg text-gray-600 mb-4">
              {t('dashboard.overview', { org: organization?.name || t('dashboard.defaultOrg') })}
            </p>
          </div>
          
          {/* Action Buttons and Meta Info */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {new Intl.DateTimeFormat(i18n.language || 'sv', { dateStyle: 'full' }).format(new Date())}
              </div>
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                {stats?.highSeverityFindings || 0} {t('dashboard.importantFindings')}
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button asChild size="lg">
                <Link to="/cases/new">
                  <FolderOpen className="h-5 w-5 mr-2" />
                  {t('dashboard.newCase')}
                </Link>
              </Button>
              <Button variant="outline" asChild size="lg">
                <Link to="/cases">
                  <Upload className="h-5 w-5 mr-2" />
                  {t('dashboard.viewCases')}
                </Link>
              </Button>
              <Button variant="outline" asChild size="lg">
                <Link to="/queue">
                  <Zap className="h-5 w-5 mr-2" />
                  {t('dashboard.viewQueue')}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/statistics?tab=audits" className="block">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 cursor-pointer hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-blue-800">{t('dashboard.stats.activeAudits')}</CardTitle>
              <FolderOpen className="h-5 w-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-900">{stats?.activeAudits || 0}</div>
              <div className="flex items-center gap-2 mt-2">
                <Progress value={stats?.totalAudits ? (stats.activeAudits / stats.totalAudits) * 100 : 0} className="flex-1 h-2" />
                <span className="text-xs text-blue-600">
                  {stats?.totalAudits || 0} {t('dashboard.stats.total')}
                  {stats?.totalAudits === 0 && " - " + t('dashboard.stats.createFirst')}
                </span>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/statistics?tab=audits" className="block">
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 cursor-pointer hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-green-800">{t('dashboard.stats.completed')}</CardTitle>
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-900">{stats?.completedAudits || 0}</div>
              <p className="text-xs text-green-600 mt-2">
                {stats?.completedAudits === 0 ? t('dashboard.stats.noCompleted') : t('dashboard.stats.completedAudits')}
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/statistics?tab=findings" className="block">
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 cursor-pointer hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-orange-800">{t('dashboard.stats.aiFindings')}</CardTitle>
              <AlertTriangle className="h-5 w-5 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-900">{stats?.totalFindings || 0}</div>
              <div className="flex items-center gap-1 mt-2">
                {stats?.totalFindings === 0 ? (
                  <span className="text-xs text-orange-600">{t('dashboard.stats.noFindings')}</span>
                ) : (
                  <Badge variant="destructive" className="text-xs">{stats?.highSeverityFindings || 0} {t('dashboard.stats.critical')}</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/statistics?tab=overview" className="block">
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 cursor-pointer hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-purple-800">{t('dashboard.stats.reports')}</CardTitle>
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-900">{stats?.totalReports || 0}</div>
              <p className="text-xs text-purple-600 mt-2">
                {stats?.totalReports === 0 ? t('dashboard.stats.noReports') : t('dashboard.stats.generatedReports')}
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Vänster kolumn - Pågående revisioner */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5" />
                    {t('dashboard.recentAudits.title')}
                  </CardTitle>
                  <CardDescription>
                    {t('dashboard.recentAudits.description')}
                  </CardDescription>
                </div>
                <Button variant="outline" asChild>
                  <Link to="/cases">{t('dashboard.recentAudits.viewAll')}</Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {recentAudits.length === 0 ? (
                <div className="text-center py-8">
                  <FolderOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('dashboard.recentAudits.empty.title')}</h3>
                  <p className="text-gray-600 mb-4">{t('dashboard.recentAudits.empty.description')}</p>
                  <Button asChild>
                    <Link to="/cases/new">
                      <FolderOpen className="h-4 w-4 mr-2" />
                      {t('dashboard.recentAudits.createNew')}
                    </Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentAudits.map((audit) => (
                    <div key={audit.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">{audit.title}</h4>
                          {audit.description && (
                            <p className="text-sm text-gray-600 mb-2">{audit.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {formatDate(audit.created_at)}
                            </span>
                          </div>
                        </div>
                        <Badge 
                          variant={audit.status === 'completed' ? 'default' : audit.status === 'pending' ? 'secondary' : 'outline'}
                        >
                          {audit.status === 'completed' ? t('dashboard.status.completed') : 
                           audit.status === 'pending' ? t('dashboard.status.pending') : 
                           audit.status === 'initial_review' ? t('dashboard.status.initialReview') :
                           audit.status === 'detailed_analysis' ? t('dashboard.status.detailedAnalysis') : t('dashboard.status.error')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Nya dashboard widgets */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RiskOverviewWidget />
            <ImpactWidget />
          </div>
        </div>

        {/* Höger kolumn - Action Items och aktivitet */}
        <div className="space-y-6">
          
          {/* Action Items */}
          <ActionItemsCard />

          {/* Senaste AI-fynd */}
          <Card className="border-orange-200 bg-orange-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-800">
                <AlertTriangle className="h-5 w-5" />
                {t('dashboard.findings.title')}
              </CardTitle>
              <CardDescription>
                {t('dashboard.findings.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recentFindings.length === 0 ? (
                <div className="text-center py-4">
                  <Shield className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">{t('dashboard.findings.empty')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentFindings.map((finding) => (
                    <div key={finding.id} className="p-3 bg-white border border-orange-200 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-gray-900 text-sm leading-tight">{finding.finding_type}</h4>
                        <Badge 
                          variant={finding.severity === 'high' || finding.severity === 'critical' ? 'destructive' : 
                                  finding.severity === 'medium' ? 'secondary' : 'outline'}
                          className="text-xs"
                        >
                          {finding.severity === 'critical' ? t('dashboard.severity.critical') :
                           finding.severity === 'high' ? t('dashboard.severity.high') : 
                           finding.severity === 'medium' ? t('dashboard.severity.medium') : t('dashboard.severity.low')}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">{finding.description}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />
                        {formatDate(finding.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  )
}